import React, { useCallback, useState, useEffect } from 'react'
import { ICellRendererParams, RowGroupOpenedEvent } from 'ag-grid-enterprise'
import { LoadingOutlined, RightOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import styled from 'styled-components'
import { EnhancedStatementRow } from '../../../utils/dataEnhancement'
import { FinancialStatementRowAction } from '../../../types'

const CustomGroupCellContainer = styled.div<{ $paddingleft: number }>`
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: center;
  padding-left: ${props => props.$paddingleft}px;
  height: 100%;
  font-size: 12px;
  width: 100%;
  gap: 2px;
  margin-left: -4px;
`

const TextContainer = styled.div`
  height: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
`

export interface NameRendererProps extends ICellRendererParams<EnhancedStatementRow> {
  onInvestmentAction?: (rowData: EnhancedStatementRow, formType: 'investment' | 'divestment') => void
  onDeprecationAction?: (rowData: EnhancedStatementRow) => void
  onAmendmentAction?: (rowData: EnhancedStatementRow) => void
  onRowAction?: (rowData: EnhancedStatementRow, action: FinancialStatementRowAction) => void
}

/**
 * Higher-Order Component that wraps a name renderer with group cell functionality
 * (expand/collapse, indentation, etc.)
 */
export const withGroupCell = <P extends NameRendererProps>(
  NameRenderer: React.ComponentType<P>
): React.FC<NameRendererProps> => {
  const GroupCell: React.FC<NameRendererProps> = props => {
    const { data, node, api, onInvestmentAction, onDeprecationAction, onAmendmentAction, onRowAction, ...restProps } =
      props

    // Track expanded state locally to trigger re-render
    const [isExpanded, setIsExpanded] = useState(node.expanded)

    // Listen to AG Grid rowGroupOpened event to detect expansion changes
    useEffect(() => {
      if (!api) return undefined

      const onRowGroupOpened = (event: RowGroupOpenedEvent) => {
        // Check if the event is for this specific node
        if (event.node.id === node.id) {
          setIsExpanded(event.node.expanded)
        }
      }

      api.addEventListener('rowGroupOpened', onRowGroupOpened)

      return () => {
        api.removeEventListener('rowGroupOpened', onRowGroupOpened)
      }
    }, [api, node.id])

    const onClick = useCallback(() => {
      node.setExpanded(!node.expanded)
    }, [node])

    if (!data) return null

    // Calculate indentation based on node level and data level
    const indentLevel = node.level
    const paddingLeft = Math.max(indentLevel * 8, 0)

    // Check if row has children OR is marked as expandable (for lazy-loaded content like dimensions)
    // node.group = true when AG Grid detects children in the tree
    // data.expandable = true for rows that will load children dynamically (DIMENSION_GROUP, REALLOCATION_MENU)
    const hasChildren = node.group === true || data.expandable === true

    return (
      <CustomGroupCellContainer $paddingleft={paddingLeft}>
        {/* Expand/Collapse arrow only if row has or can have children */}
        {hasChildren ? (
          <Button
            type="text"
            size="small"
            onClick={onClick}
            icon={data.isLoading ? <LoadingOutlined /> : <RightOutlined rotate={isExpanded ? 90 : 0} />}
            style={{
              width: '20px',
              justifySelf: 'start'
            }}
          />
        ) : (
          // Empty div spacer
          <div />
        )}

        <TextContainer>
          <NameRenderer
            {...(restProps as P)}
            data={data}
            node={node}
            api={api}
            value={data.name}
            onInvestmentAction={onInvestmentAction}
            onDeprecationAction={onDeprecationAction}
            onAmendmentAction={onAmendmentAction}
            onRowAction={onRowAction}
          />
        </TextContainer>
      </CustomGroupCellContainer>
    )
  }

  // Add display name for debugging
  GroupCell.displayName = `withGroupCell(${NameRenderer.displayName || NameRenderer.name || 'Component'})`

  return GroupCell
}
